import { useState, FormEvent } from 'react';

export default function Contact() {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!email || email.length > 500) {
      setStatus('error');
      setMessage('Please enter a valid email address (max 500 characters)');
      return;
    }

    setStatus('sending');
    
    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d6ldqpk6fojmejgaj0og', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString()
      });

      if (response.ok) {
        setStatus('success');
        setMessage('Thank you for reaching out! We will get back to you soon.');
        setEmail('');
      } else {
        setStatus('error');
        setMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Failed to send. Please try again later.');
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer id="contact" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-8">
        <div className="bg-black rounded-[48px] p-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 mb-16">
            <div className="lg:col-span-5">
              <h2 className="text-6xl font-light text-white mb-6">Stay Connected</h2>
              <p className="text-lg text-gray-400 mb-20">
                Reach out for inquiries about our applications and services
              </p>
              
              <form id="contact_form" data-readdy-form onSubmit={handleSubmit} className="space-y-8">
                <div>
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email address"
                    maxLength={500}
                    required
                    className="w-full bg-transparent border-b border-white/30 text-white placeholder-gray-500 py-4 focus:outline-none focus:border-white transition-colors text-lg"
                  />
                </div>
                
                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="bg-white text-black px-12 py-4 rounded-full font-medium hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {status === 'sending' ? 'Sending...' : 'Get in Touch →'}
                </button>
                
                {message && (
                  <p className={`text-sm ${status === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                    {message}
                  </p>
                )}
              </form>
            </div>
            
            <div className="lg:col-span-3">
              <h4 className="text-white text-xl mb-6">Navigation</h4>
              <nav className="space-y-5">
                {['home', 'about', 'products', 'features'].map((item) => (
                  <button
                    key={item}
                    onClick={() => scrollToSection(item)}
                    className="block text-gray-400 hover:text-white transition-colors capitalize cursor-pointer text-left whitespace-nowrap"
                  >
                    {item}
                  </button>
                ))}
              </nav>
            </div>
            
            <div className="lg:col-span-4">
              <h4 className="text-white text-xl mb-6">Contact Information</h4>
              <div className="space-y-4 mb-12">
                <a href="tel:+18703923587" className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-phone-line text-xl w-6 h-6 flex items-center justify-center"></i>
                  <span>+1 8703923587</span>
                </a>
                <a href="mailto:help@plenusdoo.com" className="flex items-center gap-3 text-gray-300 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-mail-line text-xl w-6 h-6 flex items-center justify-center"></i>
                  <span>help@plenusdoo.com</span>
                </a>
              </div>
              
              <h4 className="text-white text-xl mb-6">Location</h4>
              <address className="not-italic text-gray-400 space-y-2">
                <p className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-xl w-6 h-6 flex items-center justify-center mt-1"></i>
                  <span>
                    <strong className="text-gray-300">Address:</strong> Igrane 155<br />
                    <strong className="text-gray-300">City:</strong> Igrane<br />
                    <strong className="text-gray-300">Zip:</strong> 21329<br />
                    <strong className="text-gray-300">Country:</strong> Croatia
                  </span>
                </p>
              </address>
            </div>
          </div>
          
          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              <a href="https://linkedin.com" target="_blank" rel="nofollow noopener noreferrer" className="text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-linkedin-fill text-xl w-6 h-6 flex items-center justify-center"></i>
              </a>
              <a href="https://twitter.com" target="_blank" rel="nofollow noopener noreferrer" className="text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-twitter-x-fill text-xl w-6 h-6 flex items-center justify-center"></i>
              </a>
              <a href="https://github.com" target="_blank" rel="nofollow noopener noreferrer" className="text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-github-fill text-xl w-6 h-6 flex items-center justify-center"></i>
              </a>
            </div>
            
            <p className="text-gray-500 text-sm">© 2025 PLENUS d.o.o. All rights reserved</p>
            
            <div className="flex items-center gap-6">
              <span className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm whitespace-nowrap">Privacy Policy</span>
              <span className="text-gray-500">•</span>
              <span className="text-gray-400 hover:text-white transition-colors cursor-pointer text-sm whitespace-nowrap">Terms of Service</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}